import type {
  Stage,
  ThirdPartyData,
  Trim,
  TrimStage,
  TrimViewData,
  Vehicle,
  VehicleOrTrimViewData,
  VehicleStage,
  VehicleViewData
} from "@dealer-center/inventory-pages-gvm-interface";

const vehicleFields = {
  year: "Year",
  make: "Make",
  model: "Model",
  bodyStyle: "Body Style"
};

const defaultVehicle: Vehicle = {
  recordId: "",
  thirdPartyId: "",
  globalVehicleId: "",
  year: 0,
  make: "",
  model: "",
  bodyStyle: "",
  countryCd: "",
  languageCd: "",
  stage: 0,
  vehicleMapList: []
};

const defaultVehicleViewData: VehicleViewData = {
  qa: false,
  viewItem: defaultVehicle,
  editItem: defaultVehicle,
  originalItem: defaultVehicle,
  overridePresent: false,
  validFields: vehicleFields
};

export const trimFields = {
  uniqueTrim: "Unique Trim",
  trim: "Trim",
  invoicePrice: "Invoice Price",
  msrPrice: "MSRP",
  destinationCharge: "Destination Charge",
  costUom: "Currency",
  transmission: "Transmission",
  driveTrain: "Drivetrain",
  engineDisplacement: "Engine Displacement",
  engineDisplacementUom: "Engine Displacement UOM",
  engineCylinderConfig: "Engine Configuration",
  engineCylinders: "Engine Cylinders",
  horsePower: "Horsepower",
  fuelType: "Fuel Type",
  highwayFuelEff: "Highway Fuel Efficiency",
  cityFuelEff: "City Fuel Efficiency",
  fuelEffUom: "Fuel Efficiency UOM",
  doors: "Doors",
  cabType: "Cab Type",
  longTrimDescription: "Long Trim Description",
  bedSize: "Bed Size"
  // overrideVehicleId: "Model Override"
};

const defaultTrim: Trim = {
  globalVehicleId: "",
  globalVehicleTrimId: "",
  overrideVehicleId: "",
  recordId: "",
  thirdPartyId: "",
  trim: "",
  uniqueTrim: "",
  cabType: "",
  doors: 0,
  driveTrain: "",
  normalizedDriveTrain: "",
  transmission: "",
  normalizedTransmission: "",
  fuelType: "",
  cityFuelEff: 0,
  highwayFuelEff: 0,
  fuelEffUom: "",
  engineCylinderConfig: "",
  engineCylinders: 0,
  engineDisplacement: 0,
  engineDisplacementUom: "",
  horsePower: 0,
  invoicePrice: 0,
  msrPrice: 0,
  startingAtPrice: 0,
  destinationCharge: 0,
  costUom: "",
  active: true,
  bedSize: "",
  languageCd: "",
  stage: 0,
  trimMapList: [],
  longTrimDescription: ""
};

const defaultTrimViewData: TrimViewData = {
  qa: false,
  viewItem: defaultTrim,
  editItem: defaultTrim,
  originalItem: defaultTrim,
  overridePresent: false,
  validFields: trimFields
};

function hasStages(stage: Stage | undefined): boolean {
  return !!stage?.live || !!stage?.qa;
}

function hasQA(stage: Stage | undefined): boolean {
  return !!stage && !!stage.qa;
}

function getQAOrLive(stage: Stage | undefined): Vehicle | Trim | undefined {
  return stage?.qa || stage?.live;
}

function getLiveOrQA(stage: Stage | undefined): Vehicle | Trim | undefined {
  return stage?.live || stage?.qa;
}

function toViewData(
  thirdPartyData: ThirdPartyData,
  sourceType: "vehicle" | "trim"
): VehicleOrTrimViewData {
  const isQa = hasQA(thirdPartyData.oem) || hasQA(thirdPartyData.autoData);

  // DDC Overrides OEM overrides Autodata
  let isOverridePresent = false;
  let editItem: Vehicle | Trim | undefined;
  let viewItem: Vehicle | Trim | undefined;
  let originalItem: Vehicle | Trim | undefined;

  if (hasStages(thirdPartyData.ddc)) {
    isOverridePresent = true;
    viewItem = hasStages(thirdPartyData.oem)
      ? getQAOrLive(thirdPartyData.oem)
      : getQAOrLive(thirdPartyData.autoData);
    editItem = getQAOrLive(thirdPartyData.ddc);
    originalItem = hasStages(thirdPartyData.oem)
      ? getLiveOrQA(thirdPartyData.oem)
      : getLiveOrQA(thirdPartyData.autoData);
  } else if (hasStages(thirdPartyData.oem)) {
    viewItem = getLiveOrQA(thirdPartyData.oem);
    editItem = getQAOrLive(thirdPartyData.oem);
    originalItem = editItem;
  } else if (hasStages(thirdPartyData.autoData)) {
    viewItem = getLiveOrQA(thirdPartyData.autoData);
    editItem = getQAOrLive(thirdPartyData.autoData);
    originalItem = editItem;
  }

  const isVehicle = sourceType === "vehicle";
  const defaultItem: Vehicle | Trim = isVehicle ? defaultVehicle : defaultTrim;

  return {
    qa: isQa,
    viewItem: viewItem ?? defaultItem,
    editItem: editItem ?? defaultItem,
    originalItem: originalItem ?? defaultItem,
    overridePresent: isOverridePresent,
    validFields: isVehicle ? vehicleFields : trimFields
  };
}

export function toVehicleViewData(
  thirdPartyData: ThirdPartyData<VehicleStage> | undefined
): VehicleViewData {
  if (!thirdPartyData) {
    return defaultVehicleViewData;
  }
  return toViewData(thirdPartyData, "vehicle") as VehicleViewData;
}

export function toTrimViewData(
  thirdPartyData: ThirdPartyData<TrimStage> | undefined
): TrimViewData {
  if (!thirdPartyData) {
    return defaultTrimViewData;
  }
  return toViewData(thirdPartyData, "trim") as TrimViewData;
}
